import { useContext, useMemo, useState } from "react";
import { useConstant } from "@hooks/useConstant";
import { Drawer } from "@design-system/Drawer/Drawer";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { Pagination } from "@design-system/Pagination/Pagination";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { useGetHistoryDetailsQuery } from "@api/auditLog.api";
import { pluralizeWithCount } from "@common/helpers/string";
import { isHistoryDetailsResponse } from "@common/types/auditLog";
import { View } from "@common/types/views/views";
import { historyDrawerContext } from "../../historyDrawerContext";
import { getActivityDescription } from "../../utils/getActivityDescription";
import { ActivityDrawerHeader } from "../ActivityDrawerHeader";
import { useHistoryDetailsListParams } from "../useHistoryDetailsListParams";
import styles from "../HistoryDetails.module.scss";
import splitDrawerStyles from "../split/SplitHistoryDrawer.module.scss";
import {
    buildReportSettingsActivityRows,
    getReportSettingsActivityTableKind,
    ReportSettingsActivityTableKind,
    type ReportSettingsActivityRow,
} from "./reportSettingsActivityRows";
import {
    createReportSettingsActivityDetailsView,
    createReportSettingsActivityDetailsViewConfig,
} from "./reportSettingsActivityDetailsViewConfig";

const PAGE_SIZE = 20;

interface ReportSettingsActivityTableProps {
    rows: ReportSettingsActivityRow[];
    tableKind: ReportSettingsActivityTableKind;
}

const ReportSettingsActivityTable = ({
    rows,
    tableKind,
}: ReportSettingsActivityTableProps) => {
    const [view, setView] = useState<View>(() =>
        createReportSettingsActivityDetailsView(tableKind),
    );
    const config = useMemo(
        () => createReportSettingsActivityDetailsViewConfig(tableKind),
        [tableKind],
    );
    return (
        <ViewTable
            data={rows}
            view={view}
            config={config}
            onViewChange={setView}
            emptyView={() => <EmptyState header="No changes found" />}
            className={styles.historyDetailsView}
        />
    );
};

export const ReportSettingsActivityHistoryDrawer = () => {
    const { open, historyRow, closeDrawer } = useContext(historyDrawerContext);
    const [isOpen, setIsOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const shouldShowContent = isOpen || open;
    const listParams = useHistoryDetailsListParams(historyRow);
    const fallbackDate = useConstant(() => new Date());

    const { data, isLoading, isError, refetch } = useGetHistoryDetailsQuery({
        ...listParams,
        date: historyRow?.date ?? fallbackDate,
        sourceType: historyRow?.sourceType ?? "",
        resourceType: historyRow?.resourceType,
        actorId: historyRow?.actorId,
        entityIds: historyRow?.entityIds,
        changeTypes: historyRow?.changeTypes,
        fields: historyRow?.fields,
        sourceIds: historyRow?.historyItem.sourceIds,
        page: currentPage,
        limit: PAGE_SIZE,
        options: {
            enabled:
                shouldShowContent && !!historyRow && !!historyRow.sourceType,
        },
    });

    const response = data && isHistoryDetailsResponse(data) ? data : undefined;
    const sourceType = historyRow?.sourceType ?? "";
    const tableKind = getReportSettingsActivityTableKind(sourceType);
    const rows = useMemo(
        () =>
            buildReportSettingsActivityRows({
                sourceType,
                details: response?.data ?? [],
            }),
        [response?.data, sourceType],
    );

    const footer = response ? (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <div className={styles.footerLeft}>
                {response.totalPages > 1 && (
                    <Pagination
                        size="md"
                        currentPage={currentPage}
                        pageCount={response.totalPages}
                        onPageChange={setCurrentPage}
                    />
                )}
                <CommandIslandTotal>
                    {pluralizeWithCount("event", response.total)}
                </CommandIslandTotal>
            </div>
        </CommandIsland>
    ) : null;

    const renderContent = () => {
        if (isLoading || !historyRow) {
            return (
                <div className={splitDrawerStyles.loaderContainer}>
                    <Loader />
                </div>
            );
        }
        if (isError || !response) {
            return (
                <div className={splitDrawerStyles.emptyContainer}>
                    <EmptyState
                        header="Failed to load changes"
                        ctaText="Retry"
                        action={() => {
                            void refetch();
                        }}
                    />
                </div>
            );
        }
        if (rows.length === 0) {
            return (
                <div className={splitDrawerStyles.emptyContainer}>
                    <EmptyState header="No changes found" />
                </div>
            );
        }
        return (
            <>
                <ReportSettingsActivityTable
                    key={`${tableKind}-${historyRow.id}`}
                    rows={rows}
                    tableKind={tableKind}
                />
                {footer}
            </>
        );
    };

    return (
        <Drawer
            open={open}
            onClose={closeDrawer}
            direction="right"
            onAnimationEnd={setIsOpen}
            size="fullScreenWithNav"
            noPadding
            headerAddonLeft={
                historyRow ? (
                    <ActivityDrawerHeader
                        historyRow={historyRow}
                        activityTitle={getActivityDescription(historyRow)}
                    />
                ) : null
            }
        >
            {shouldShowContent && (
                <div className={styles.drawerContent}>{renderContent()}</div>
            )}
        </Drawer>
    );
};
